/** Opens Hub Reports and starts the walkthrough. */
export const REPORTS_TOUR_HREF = '/hub?openTab=Reports&reportsTour=1';

export const REPORTS_TOUR_PARAM = 'reportsTour';

export type ReportsTourStepId =
  | 'intro'
  | 'filter'
  | 'tabs'
  | 'createDraft'
  | 'opord'
  | 'templateOptions'
  | 'createConfirm'
  | 'draftCard'
  | 'sections'
  | 'aiGenerate'
  | 'draftMenu'
  | 'submitReview';

export type ReportsTourStepDef = {
  id: ReportsTourStepId;
  title: string;
  description: string;
  /** `data-tour` value. */
  tour?: string;
  /**
   * Raw selector, for elements that exist on several cards at once and must be
   * scoped to the draft the tour created.
   */
  selector?: string;
};

/** Selector prefix that scopes a step to the draft card the tour created. */
export const REPORTS_TOUR_DRAFT_CARD = '[data-tour="reports-draft-card"]';

/** Tabs step when Report versioning replaces Current / Historical / Drafts. */
export const REPORTS_VERSIONING_TABS_DESCRIPTION =
  'Each report is one card. Its version bar switches versions and, once the latest report is published, Create offers New draft version. There are no Current, Historical, or Drafts tabs.';

/** Create step when the empty-list button or the version bar may be showing. */
export const REPORTS_VERSIONING_CREATE_DESCRIPTION =
  'Create Draft Report, on an empty list, starts a report from a template. On a published report, open Create on the version bar and choose New draft version. Next opens the template dialog when a new draft can be started from here.';

/** Matches the empty-list button or the version bar Create control. */
export const REPORTS_VERSIONING_CREATE_SELECTOR =
  '[data-tour="reports-create-draft"], [data-tour="reports-new-draft-version"]';

export const REPORTS_TOUR_STEPS: ReportsTourStepDef[] = [
  {
    id: 'intro',
    tour: 'reports-intro',
    title: 'Reports',
    description:
      'This is where you write situation reports and operations orders for an area of responsibility or an incident. This walkthrough opens an existing draft when one is already in progress, and creates a real draft only when the scope has none.',
  },
  {
    id: 'filter',
    tour: 'reports-filter',
    title: 'Choose a place or incident',
    description:
      'An AOR is selected when Reports opens, so the list can load. Switch to another AOR or an incident if you want a different scope. Next picks an AOR when none is selected yet.',
  },
  {
    id: 'tabs',
    tour: 'reports-tabs',
    title: 'Current, history, and drafts',
    description:
      'Current is the latest published report. Historical is past published reports. Drafts is unpublished work. An incident report does not wait for sign-off. An area report with an approval policy does.',
  },
  {
    id: 'createDraft',
    tour: 'reports-create-draft',
    title: 'Start a draft report',
    description:
      'Create Draft Report starts a new report from a template. Next opens that dialog so you can see the options.',
  },
  {
    id: 'opord',
    tour: 'reports-opord',
    title: 'The OPORD template',
    description:
      'OPORD is the operations-order template. After you create a draft, you fill these categories and sections:<br><br><strong>Situation</strong> — a category for the operating area, the hazard or threat, and related context. AI can help draft the sections.<br><br><strong>Mission</strong> — one clear tasking sentence: who, what, when, where, and why.<br><br><strong>Execution</strong> — a category for how the operation unfolds (Concept of Operations), plus Tasks that draft Planned actions. Those tasks also appear in the Actions tracker.<br><br><strong>Administration and Logistics</strong> — a category for sustainment, supply, personnel, public affairs, and medical support.<br><br><strong>Command and Control</strong> — a category for who is in charge and how units communicate.',
  },
  {
    id: 'templateOptions',
    tour: 'reports-template-options',
    title: 'Other templates, or your own',
    description:
      'America 250 events and USDA are system templates, and any templates your organization has saved appear here with an Org badge. Clone copies a template so you can rearrange or rename its sections before creating the draft, and Create new template starts from a blank outline. Either way the result is saved as a new org template and reused next time.',
  },
  {
    id: 'createConfirm',
    tour: 'reports-create-confirm',
    title: 'Create the draft',
    description:
      'If this scope already has a draft, Next closes this dialog and opens that report for an overview. It does not start another one. A scope with no draft yet is created for real with the OPORD template selected.',
  },
  {
    id: 'draftCard',
    tour: 'reports-draft-card',
    title: 'Your new draft',
    description:
      'This is an overview of the draft on this scope. The header badge reads Incident: [name] or the area name, plus who last edited it and when. An incident report’s map area is the incident location (Edit Incident Location). An area report uses the area (Edit AOR on Map). There is no report-wide Save — every section saves on its own.',
  },
  {
    id: 'sections',
    selector: `${REPORTS_TOUR_DRAFT_CARD} [data-tour="reports-sections"]`,
    title: 'Sections are the unit of work',
    description:
      'A new draft opens as one continuous document with an outline beside it. Sections are grouped into categories where the template defines them. Click a section to edit it; Save on that section writes it. There is no report-wide Save. Tabs layout, from the card menu, shows one section at a time. Add Section in that same menu appends a blank manual section. Drag the outline to reorder.',
  },
  {
    id: 'aiGenerate',
    selector: `${REPORTS_TOUR_DRAFT_CARD} [data-tour="reports-ai-generate"]`,
    title: 'Let AI write the first pass',
    description:
      'A sparkle on a section runs AI Generate for that section. AI Generate All, on a category, writes every AI section in that category. Both pull from the data sources in Section Settings — layers, events, and actions — using the incident location or the area as the map boundary. Someone else generating the same section locks it until they finish.',
  },
  {
    id: 'draftMenu',
    selector: `${REPORTS_TOUR_DRAFT_CARD} [data-tour="reports-draft-menu"]`,
    title: 'The draft menu',
    description:
      'Layout, Presentation mode, Quad Chart, export, Add Section, Add Files (incident reports), Set Refresh Time Interval, Publish or Submit for review, and Delete draft live in this menu. Area reports also show Approval settings, locked with “Upgrade to unlock approval workflows.” Next opens the menu.',
  },
  {
    id: 'submitReview',
    tour: 'reports-submit-review',
    title: 'Publish or submit',
    description:
      'An incident draft’s menu says Publish. It becomes the Current report with no approval. An area draft with approval steps says Submit for review. An area with no approval steps publishes immediately. This walkthrough does not press it. The draft on screen is yours to keep or remove with Delete draft, just below.',
  },
];

export function reportsTourStepIds(): ReportsTourStepId[] {
  return REPORTS_TOUR_STEPS.map((step) => step.id);
}

export function reportsTourStepTitles(): string[] {
  return REPORTS_TOUR_STEPS.map((step) => step.title);
}
